import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import React from 'react';
import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import classNames from 'classnames';
import { ClearAllIcon } from '@cognite/cogs-icons';
import { getThemeClassNames } from '@cognite/cogs-utils';
import { HelpText } from '../_technical/help-text/help-text.js';
import { Message } from '../_technical/message/message.js';
import { TextLabel } from '../_technical/text-label/text-label.js';
import { Button } from '../button/button.js';

/**
 * Textarea
 * =============================================================================
 * Describe what your component does, and why it could be useful for users.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-textarea--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Textarea>
 *   My textarea
 * </Textarea>
 *
 * <Textarea subject="bird">
 *   My advanced textarea
 * </Textarea>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param subject - Explain here what your prop does
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-textarea--base)
 */
var Textarea = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var appearance = _a.appearance,
    autoResize = _a.autoResize,
    className = _a.className,
    disabled = _a.disabled,
    error = _a.error,
    fullWidth = _a.fullWidth,
    helpText = _a.helpText,
    helpTextIcon = _a.helpTextIcon,
    info = _a.info,
    label = _a.label,
    onChange = _a.onChange,
    onClear = _a.onClear,
    onKeyDown = _a.onKeyDown,
    onKeyUp = _a.onKeyUp,
    required = _a.required,
    resizable = _a.resizable,
    value = _a.value,
    _a$rows = _a.rows,
    rows = _a$rows === undefined ? 3 : _a$rows,
    inverted = _a.inverted,
    themeProps = _a.themeProps,
    otherProps = __rest(_a, ["appearance", "autoResize", "className", "disabled", "error", "fullWidth", "helpText", "helpTextIcon", "info", "label", "onChange", "onClear", "onKeyDown", "onKeyUp", "required", "resizable", "value", "rows", "inverted", "themeProps"]);
  var textareaProps = Object.assign({
    className: 'cogs-textarea__textarea',
    onChange: onChange,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    disabled: disabled,
    value: value,
    required: required,
    ref: ref
  }, otherProps);
  return jsxs("label", {
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-textarea', "cogs-textarea--" + appearance, fullWidth && 'cogs-textarea--full-width', resizable && 'cogs-textarea--resizable', resizable === true && 'cogs-textarea--resizable-both', resizable === 'horizontal' && 'cogs-textarea--resizable-horizontal', resizable === 'vertical' && 'cogs-textarea--resizable-vertical', disabled && 'cogs-textarea--disabled', !!error && 'cogs-textarea--error'].concat(getThemeClassNames(inverted, themeProps), [className])),
    "data-testid": "cogs-textarea",
    children: [label && appearance !== 'ghost' && jsx(TextLabel, {
      text: label,
      required: required,
      info: info
    }), jsxs("span", {
      className: classNames('cogs-v10', 'cogs-textarea-container'),
      children: [autoResize ? jsx(TextareaAutosize, Object.assign({
        minRows: rows
      }, textareaProps)) : jsx("textarea", Object.assign({
        rows: rows
      }, textareaProps)), onClear && value && jsx(Button, {
        type: "ghost",
        icon: jsx(ClearAllIcon, {}),
        size: "small",
        className: classNames('cogs-v10', 'cogs-textarea', 'cogs-textarea--on-clear'),
        onClick: onClear,
        onKeyDown: onKeyDown,
        onKeyUp: onKeyUp
      })]
    }), helpText && jsx(HelpText, {
      text: helpText,
      icon: helpTextIcon,
      appearance: appearance
    }), !!error && jsx(Message, {
      text: error,
      appearance: "error"
    })]
  });
});

export { Textarea };
